import type { API } from 'chessboard/plugins/highlights/config/types';
import { Core } from 'chessboard/core/config/types';
import type { UpdateParameters } from 'chessboard/plugins/highlights/api';
import { isNotOneOf } from 'src/utils/app/model/is-not-one-of';
import { is } from 'src/utils/app/model/is';
import { init } from 'chessboard/plugins/highlights/event-handlers/init';
import type { Plugins } from 'chessboard/core/plugins/config/types';

const checkBefore = (handler: Function) => (event: Core.Event, context: Core.Context) => {
  const isSetupMode = context.api.getMode().name === 'setup';
  const setupImprovements = context.api.getOptions().setupImprovements;

  if (setupImprovements && isSetupMode) {
    return;
  }

  handler(event, context);
};

/**
 * Event handlers for the highlights plugin.
 */
export function createHighlightsEventHandlers(): Plugins.Match[] {
  return [
    {
      condition: isNotOneOf([
        Core.UserEvents.Pointermove,
        Core.InstanceEvents.RendererSet,
        Core.APIEvents.NodeUpdated,
      ]),
      handler: checkBefore(updateHighlights),
    },
    {
      condition: is(Core.InstanceEvents.RendererSet),
      handler: checkBefore(onRendererSet),
    },
  ];

  /**
   * re-initialize highlights plugin on RendererSet event
   */
  function onRendererSet(_event: Core.Event, context: Core.Context) {
    init(context);
  }

  /**
   * Update the highlights for a given event.
   */
  function updateHighlights(event: Core.Event, context: Core.Context) {
    const { api, renderer } = context;

    if (!renderer) {
      return;
    }

    const highlights = <API<UpdateParameters>>api.highlights;

    const { toAdd, toRemove } = highlights.update(event, context);

    renderer.removeHighlights(toRemove, context);
    renderer.addHighlights(toAdd, context);
  }
}
